import { GELATO_API } from '@/constants/api';
import { GelatoStatusResponse, GetUserTaskReceiptReturnType, TaskState } from '@/types/gelato';
import { JsonRpcProvider, TransactionResponse } from '@ethersproject/providers';
import { registerGelatoTaskHooks } from './gelatoEventListener';

export const getTaskStatus = async (taskId: string): Promise<GelatoStatusResponse | undefined> => {
  try {
    return (await fetch(`${GELATO_API}/tasks/status/${taskId}`).then((response) => response.json()))?.task;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    throw new Error(
      `GelatoSmartWalletSDK/getTaskStatus: Failed with error: ${
        error.response?.data?.message ?? error.message ?? 'Internal Error'
      }`,
    );
  }
};

const hasNotifiedTransactionHashMap = new Map<string, boolean>();

/**
 * Listen to task status updates via WebSocket events instead of polling
 * This is the recommended approach for better performance and real-time updates
 */
export async function listenToTaskStatus(
  taskId: string,
  hooks?: {
    onHasTransactionHash?: (taskId: string, transactionHash: string) => void;
    onTaskCompleted?: (taskId: string, status: GelatoStatusResponse) => void;
    onTaskFailed?: (taskId: string, status: GelatoStatusResponse, reason?: string) => void;
  },
): Promise<GelatoStatusResponse> {
  return new Promise((resolve, reject) => {
    const hasNotifiedTransactionHash = hasNotifiedTransactionHashMap.get(taskId) || false;

    registerGelatoTaskHooks(taskId, {
      onTransactionHash: (taskId: string, transactionHash: string) => {
        if (!hasNotifiedTransactionHash) {
          hooks?.onHasTransactionHash?.(taskId, transactionHash);
          hasNotifiedTransactionHashMap.set(taskId, true);
        }
      },
      onTaskCompleted: (taskId: string, taskStatus) => {
        const gelatoStatus: GelatoStatusResponse = {
          taskId: taskStatus.taskId,
          taskState: taskStatus.taskState as unknown as TaskState,
          chainId: taskStatus.chainId,
          creationDate: taskStatus.creationDate,
          executionDate: taskStatus.executionDate,
          transactionHash: taskStatus.transactionHash,
          blockNumber: taskStatus.blockNumber,
          gasUsed: taskStatus.gasUsed,
          effectiveGasPrice: taskStatus.effectiveGasPrice,
          lastCheckDate: taskStatus.lastCheckDate,
          lastCheckMessage: taskStatus.lastCheckMessage,
        };
        hooks?.onTaskCompleted?.(taskId, gelatoStatus);
        resolve(gelatoStatus);
      },
      onTaskFailed: (taskId: string, taskStatus, reason) => {
        const gelatoStatus: GelatoStatusResponse = {
          taskId: taskStatus.taskId,
          taskState: taskStatus.taskState as unknown as TaskState,
          chainId: taskStatus.chainId,
          creationDate: taskStatus.creationDate,
          executionDate: taskStatus.executionDate,
          transactionHash: taskStatus.transactionHash,
          blockNumber: taskStatus.blockNumber,
          gasUsed: taskStatus.gasUsed,
          effectiveGasPrice: taskStatus.effectiveGasPrice,
          lastCheckDate: taskStatus.lastCheckDate,
          lastCheckMessage: taskStatus.lastCheckMessage,
        };
        hooks?.onTaskFailed?.(taskId, gelatoStatus, reason);
        reject({ status: gelatoStatus, reason });
      },
    });
  });
}

/**
 * Wait for task completion using WebSocket events
 * This replaces pollTaskStatus for better performance
 */
export const waitForTaskCompletion = async (taskId: string): Promise<GelatoStatusResponse> => {
  return new Promise((resolve, reject) => {
    registerGelatoTaskHooks(taskId, {
      onTransactionHash: () => {
        // No need to handle the transaction hash; just wait for completion
      },
      onTaskCompleted: (taskId: string, taskStatus) => {
        const gelatoStatus: GelatoStatusResponse = {
          taskId: taskStatus.taskId,
          taskState: taskStatus.taskState as unknown as TaskState,
          chainId: taskStatus.chainId,
          creationDate: taskStatus.creationDate,
          executionDate: taskStatus.executionDate,
          transactionHash: taskStatus.transactionHash,
          blockNumber: taskStatus.blockNumber,
          gasUsed: taskStatus.gasUsed,
          effectiveGasPrice: taskStatus.effectiveGasPrice,
          lastCheckDate: taskStatus.lastCheckDate,
          lastCheckMessage: taskStatus.lastCheckMessage,
        };
        resolve(gelatoStatus);
      },
      onTaskFailed: (taskId: string, taskStatus, reason) => {
        const gelatoStatus: GelatoStatusResponse = {
          taskId: taskStatus.taskId,
          taskState: taskStatus.taskState as unknown as TaskState,
          chainId: taskStatus.chainId,
          creationDate: taskStatus.creationDate,
          executionDate: taskStatus.executionDate,
          transactionHash: taskStatus.transactionHash,
          blockNumber: taskStatus.blockNumber,
          gasUsed: taskStatus.gasUsed,
          effectiveGasPrice: taskStatus.effectiveGasPrice,
          lastCheckDate: taskStatus.lastCheckDate,
          lastCheckMessage: taskStatus.lastCheckMessage,
        };
        reject({ status: gelatoStatus, reason });
      },
    });
  });
};

// Poll until task reaches final state or timeout
const maxAttempts = 60 * 5 * 2; // 60 attempts with 500ms interval = 30s total
const pollInterval = 500;
export async function pollTaskStatus(
  taskId: string,
  hooks?: {
    onHasTransactionHash?: (taskId: string, transactionHash: string) => void;
  },
): Promise<GelatoStatusResponse> {
  let status = await getTaskStatus(taskId);

  let attempts = 0;
  let hasNotifiedTransactionHash = hasNotifiedTransactionHashMap.get(taskId) || false; // Track if we've already notified about transaction hash

  // Check and notify about transaction hash only once
  if (status?.transactionHash && !hasNotifiedTransactionHash) {
    hooks?.onHasTransactionHash?.(taskId, status.transactionHash);
    hasNotifiedTransactionHashMap.set(taskId, true);
  }

  while (!status?.taskState || !isFinalTaskState(status.taskState, status.transactionHash)) {
    if (attempts >= maxAttempts) {
      throw new Error('Task status polling timeout');
    }

    // Wait pollInterval before next poll
    await new Promise((resolve) => setTimeout(resolve, pollInterval));
    status = await getTaskStatus(taskId);
    hasNotifiedTransactionHash = hasNotifiedTransactionHashMap.get(taskId) || false; // Update hasNotifiedTransactionHash after polling

    // Check and notify about transaction hash only once
    if (status?.transactionHash && !hasNotifiedTransactionHash) {
      hooks?.onHasTransactionHash?.(taskId, status.transactionHash);
      hasNotifiedTransactionHashMap.set(taskId, true);
    }
    attempts++;
  }

  return status;
}

/**
 * Get user task receipt - simplified version that just calls pollTaskStatus and getUserTaskReceiptFromTaskStatus
 * For detailed timing, use the individual functions directly
 */
export async function getUserTaskReceipt(
  provider: JsonRpcProvider,
  taskId: string,
): Promise<GetUserTaskReceiptReturnType> {
  const status = await pollTaskStatus(taskId);

  if (
    status.taskState === TaskState.Cancelled ||
    status.taskState === TaskState.ExecReverted ||
    !status.transactionHash
  ) {
    return {
      success: false,
      reason: status.lastCheckMessage,
      taskId: taskId,
    } as GetUserTaskReceiptReturnType;
  }

  return getUserTaskReceiptFromTaskStatus(provider, status);
}

export async function getUserTaskReceiptFromTaskStatus(
  provider: JsonRpcProvider,
  statusResponse: GelatoStatusResponse,
): Promise<GetUserTaskReceiptReturnType> {
  const receipt = await provider.getTransactionReceipt(statusResponse.transactionHash || '');

  return {
    success: true,
    taskId: statusResponse.taskId || '',
    transactionReceipt: receipt,
    status: statusResponse,
  };
}

export async function getUserTaskResponse(
  provider: JsonRpcProvider,
  status: GelatoStatusResponse,
): Promise<GetUserTaskReceiptReturnType & { response?: TransactionResponse }> {
  if (!provider) {
    throw new Error('Provider is required for getUserTaskResponse');
  }

  if (!status.transactionHash) {
    throw new Error('Transaction hash is required for getUserTaskResponse');
  }

  const transactionResponse = await provider.getTransaction(status.transactionHash);

  return {
    success: true,
    taskId: status.taskId || '',
    response: transactionResponse,
    status: status,
  };
}

export const isFinalTaskState = (taskState: TaskState, transactionHash?: string): boolean => {
  if (transactionHash) {
    // return true;
  }
  switch (taskState) {
    case TaskState.ExecSuccess:
    case TaskState.ExecReverted:
    case TaskState.Cancelled:
      return true;
    default:
      return false;
  }
};

export const isSubmitted = (taskState: TaskState): boolean => {
  return taskState === TaskState.ExecPending || taskState === TaskState.WaitingForConfirmation;
};
